/**
 * Web Agora call hook — uses the shared rtcClient from AgoraRTCProvider
 * with IMPERATIVE track creation and publishing.
 *
 * Why imperative instead of useLocalMicrophoneTrack / usePublish?
 *   - Declarative hooks may access hardware (camera/mic) prematurely,
 *     causing browser/OS indicators to light up before the user opts in.
 *   - usePublish doesn't reliably handle dynamic track-set changes
 *     (e.g. adding a mic track after the initial render).
 *
 * Relies on the AgoraRTCProvider already mounted in _layout.tsx
 * so useJoin / useRemoteUsers still read from the same shared rtcClient.
 */

import { useState, useCallback, useMemo, useEffect, useRef, useSyncExternalStore } from 'react'
import AgoraRTC, { useJoin, useRemoteUsers, useRTCClient } from 'agora-rtc-react'
import type { IAgoraRTCRemoteUser, ICameraVideoTrack, IMicrophoneAudioTrack } from 'agora-rtc-react'
import type { JoinMode, AgoraRemoteUser, AgoraCallState, AgoraCallActions } from './types'

export type { AgoraRemoteUser, AgoraCallState, AgoraCallActions }

const AGORA_APP_ID = process.env.EXPO_PUBLIC_AGORA_APP_ID ?? '06997babc99845ef943d90ee04cb1a99'

/**
 * Module-level store for the local camera track so that
 * PlayerCellVideo.web.tsx can read it without creating its own.
 */
let _cameraTrack: ICameraVideoTrack | null = null
const _cameraListeners = new Set<() => void>()

function setCameraTrackGlobal(track: ICameraVideoTrack | null) {
  _cameraTrack = track
  for (const cb of _cameraListeners) cb()
}

const subscribeCameraTrack = (cb: () => void) => {
  _cameraListeners.add(cb)
  return () => {
    _cameraListeners.delete(cb)
  }
}
const getCameraTrackSnapshot = () => _cameraTrack

/**
 * Web-only hook — returns the current local camera track (or null).
 * Triggers a re-render when the track is created or destroyed.
 */
export function useWebLocalCameraTrack(): ICameraVideoTrack | null {
  return useSyncExternalStore(subscribeCameraTrack, getCameraTrackSnapshot, getCameraTrackSnapshot)
}

type RtcClient = ReturnType<typeof useRTCClient>

async function subscribeAndPlayAudio(client: RtcClient, user: IAgoraRTCRemoteUser) {
  try {
    await client.subscribe(user, 'audio')
    user.audioTrack?.play()
  } catch (err) {
    console.error('[AgoraWeb] remote audio subscribe failed', user.uid, err)
  }
}

/**
 * Ensures all players hear broadcasters immediately — no component
 * rendering required, works even in receive-only mode.
 */
function useRemoteAudioAutoplay(isConnected: boolean, client: RtcClient | null) {
  useEffect(() => {
    if (!isConnected) return
    if (!client) return

    const handlePublished = (user: IAgoraRTCRemoteUser, mediaType: 'audio' | 'video') => {
      if (mediaType === 'audio') subscribeAndPlayAudio(client, user)
    }

    const handleUnpublished = (user: IAgoraRTCRemoteUser, mediaType: 'audio' | 'video') => {
      if (mediaType === 'audio') user.audioTrack?.stop()
    }

    client.on('user-published', handlePublished)
    client.on('user-unpublished', handleUnpublished)

    for (const user of client.remoteUsers) {
      if (user.hasAudio) subscribeAndPlayAudio(client, user)
    }

    return () => {
      client.off('user-published', handlePublished)
      client.off('user-unpublished', handleUnpublished)
    }
  }, [isConnected, client])
}

export function useAgoraCall(): [AgoraCallState, AgoraCallActions] {
  const [joinConfig, setJoinConfig] = useState<{
    token: string
    channel: string
    uid: number
  } | null>(null)

  const [micEnabled, setMicEnabled] = useState(false)
  const [cameraEnabled, setCameraEnabled] = useState(false)

  let client: ReturnType<typeof useRTCClient> | null = null
  try {
    client = useRTCClient()
  } catch {
    console.error('[AgoraWeb] useRTCClient failed — is AgoraRTCProvider mounted?')
  }

  // ─── Declarative join (still fine — no hardware access) ────────────
  const {
    data: joinUid,
    isLoading: joinLoading,
    isConnected,
    error: joinError
  } = useJoin(
    joinConfig
      ? {
          appid: AGORA_APP_ID,
          channel: joinConfig.channel,
          token: joinConfig.token,
          uid: joinConfig.uid
        }
      : { appid: '', channel: '', token: null },
    !!joinConfig
  )

  useEffect(() => {
    if (joinError) console.error('[AgoraWeb] join error', joinError)
  }, [joinError])

  // ─── Imperative mic track management ───────────────────────────────
  const micTrackRef = useRef<IMicrophoneAudioTrack | null>(null)

  useEffect(() => {
    if (!isConnected) return
    if (!client) return

    if (micEnabled) {
      let cancelled = false
      const create = async () => {
        try {
          const track = await AgoraRTC.createMicrophoneAudioTrack({ ANS: true, AEC: true })
          if (cancelled) {
            track.close()
            return
          }
          micTrackRef.current = track
          await client.publish([track])
        } catch (err) {
          console.error('[AgoraWeb] mic track error', err)
        }
      }
      create()
      return () => {
        cancelled = true
        const track = micTrackRef.current
        if (track) {
          client.unpublish([track]).catch(() => {})
          track.close()
          micTrackRef.current = null
        }
      }
    }

    // micEnabled is false — ensure track is cleaned up
    const track = micTrackRef.current
    if (track) {
      client.unpublish([track]).catch(() => {})
      track.close()
      micTrackRef.current = null
    }
    return undefined
  }, [isConnected, micEnabled, client])

  // ─── Imperative camera track management ────────────────────────────
  const camTrackRef = useRef<ICameraVideoTrack | null>(null)

  useEffect(() => {
    if (!isConnected) return
    if (!client) return

    if (cameraEnabled) {
      let cancelled = false
      const create = async () => {
        try {
          const track = await AgoraRTC.createCameraVideoTrack()
          if (cancelled) {
            track.close()
            return
          }
          camTrackRef.current = track
          setCameraTrackGlobal(track)
          await client.publish([track])
        } catch (err) {
          console.error('[AgoraWeb] camera track error', err)
        }
      }
      create()
      return () => {
        cancelled = true
        const track = camTrackRef.current
        if (track) {
          client.unpublish([track]).catch(() => {})
          track.close()
          camTrackRef.current = null
          setCameraTrackGlobal(null)
        }
      }
    }

    const track = camTrackRef.current
    if (track) {
      client.unpublish([track]).catch(() => {})
      track.close()
      camTrackRef.current = null
      setCameraTrackGlobal(null)
    }
    return undefined
  }, [isConnected, cameraEnabled, client])

  // Cleanup global ref on unmount
  useEffect(() => {
    return () => {
      setCameraTrackGlobal(null)
    }
  }, [])

  // ─── Auto-subscribe & play remote audio ────────────────────────────
  useRemoteAudioAutoplay(isConnected, client)

  // ─── Remote users (declarative — fine, no hardware) ────────────────
  const rawRemoteUsers: IAgoraRTCRemoteUser[] = useRemoteUsers()

  const remoteUsers: AgoraRemoteUser[] = useMemo(
    () =>
      rawRemoteUsers.map((u) => ({
        uid: Number(u.uid),
        hasAudio: u.hasAudio,
        hasVideo: u.hasVideo
      })),
    [rawRemoteUsers]
  )

  // ─── Actions ───────────────────────────────────────────────────────
  const join = useCallback((token: string, channel: string, uid: number, mode: JoinMode) => {
    setMicEnabled(mode !== 'off')
    setCameraEnabled(mode === 'video')
    setJoinConfig({ token, channel, uid })
  }, [])

  const leave = useCallback(() => {
    setJoinConfig(null)
    setMicEnabled(false)
    setCameraEnabled(false)
  }, [])

  const toggleMic = useCallback((enabled: boolean) => {
    setMicEnabled(enabled)
  }, [])

  const toggleCamera = useCallback((enabled: boolean) => {
    setCameraEnabled(enabled)
  }, [])

  const state: AgoraCallState = {
    isConnected,
    isPublishing: micEnabled || cameraEnabled,
    localMicEnabled: micEnabled,
    localCameraEnabled: cameraEnabled,
    remoteUsers
  }

  const actions: AgoraCallActions = useMemo(
    () => ({ join, leave, toggleMic, toggleCamera }),
    [join, leave, toggleMic, toggleCamera]
  )

  return [state, actions]
}
